import { useCallback, useMemo } from 'react'
import { useRootStore } from '@/contexts/root-store-context'
import { useObservableValue } from '@/hooks/rx-hooks'
import { map, merge } from 'rxjs'

/**
 * Reads the specified CSS variable from the document.
 *
 * @param name Name of the variable to read, e.g. `--surface-background`.
 * @param target The target to read the variable from. Defaults to `document.body`.
 */
export function useCssVariable(name: string, target?: Element | null): string {
  target ??= document.body

  // Since the theme and color scheme affects the values of CSS variables,
  // we'll re-read them when any of those change.
  const {
    shell: { theme$, colorScheme$ },
  } = useRootStore()

  // Read the value of the variable.
  const read = useCallback(() => readCssVar(target, name), [name, target])

  return useObservableValue(
    useMemo(
      () => merge(theme$, colorScheme$).pipe(map(read)),
      [theme$, colorScheme$, read],
    ),
    {
      initial: read,
    },
  )
}

/**
 * Reads the CSS variable from the element's computed style.
 * @param element
 * @param name
 */
function readCssVar(element: Element, name: string) {
  const style = getComputedStyle(element)
  return style.getPropertyValue(name)
}
